/**
 * Module for communicating with Yealink headsets
 * - will use WebHID
 */

import globalEvents from '@/utilities/GlobalEvents'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import logger from '@/utilities/Logger'
import type { MediaDevice } from '@/stores/MediaDevices.types'
import { parseUsbProductId, findMatchingYealinkHid, isYealinkMediaDevice } from './yealinkMatching'
import { ref, type Ref } from 'vue'

export default class Yealink {
	static #instance: Yealink // NOSONAR

	use: Ref<boolean> = ref(false)

	useDeviceName: Ref<string | undefined> = ref(undefined)

	private hidDevice?: HIDDevice = undefined

	private holdActive = false

	private lastAction = -1

	private readonly boundClose: () => void = () => { /** */ }

	private readonly boundHeadsetEvent: (event: HIDInputReportEvent) => void = (_) => { /** */ }

	private eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void = () => { /** */ }

	private constructor() {
		logger.debug('Yealink: Constructor')

		globalEvents.on('MediaDeviceChange', event => { this.mediaChanged(event.kind) })

		globalEvents.on('MediaDeviceSelected', data => { this.mediaChanged(data.type) })

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'yealink', value: this.use.value })
			globalEvents.emit('StatusReport', { key: 'yealinkIntegration', value: 'WebHID' })
			if (this.use.value) {
				globalEvents.emit('StatusReport', { key: 'yealinkDevice', value: this.useDeviceName.value as string })
			}
		})

		this.boundClose = this.close.bind(this)
		this.boundHeadsetEvent = this.headsetEvent.bind(this)
	}

	/** Get the Yealink singleton instance */
	static get instance() {
		return Yealink.#instance
	}

	/** Create the Yealink singleton instance and return it */
	static create(eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void) {
		if (!Yealink.#instance) {
			Yealink.#instance = new Yealink()
		}
		Yealink.#instance.eventCallback = eventCallback
		return Yealink.#instance
	}

	mediaChanged(kind?: string) {
		if (kind === 'audioinput') { // MediaDeviceChange / MediaDeviceSelected
			const mediaDevices = useMediaDevicesStore()
			const device = mediaDevices.microphoneDevice

			if (device && isYealinkMediaDevice(device)) {
				if (this.isOpen) { this.close() }
				this.init(device)
			} else {
				if (this.isOpen) { this.close() }
				if (this.hidDevice) { // just to be sure
					this.hidDevice.removeEventListener('beforeunload', this.boundClose)
				}
				this.hidDevice = undefined
				this.use.value = false
				this.useDeviceName.value = undefined
				logger.debug('Yealink: Selected device is not Yealink - terminating')
			}
		}
	}

	// #region Init

	/**
	 * Setup what needs to be setup to initialize the headset device
	 * @param audioDevice Audio device we want to control
	 */
	private async init(audioDevice: MediaDevice) {
		logger.info(`Yealink: Initializing - looking for ${audioDevice.label} with ID ${audioDevice.productId}`)
		this.use.value = false
		this.useDeviceName.value = undefined

		// Do we have access to the device that matches the SELECTED audio device (matched on its
		// USB product id). With two Yealinks connected, matching on vendor id alone would bind
		// call control to whichever Yealink enumerated first - possibly the wrong headset.
		// Yealink also presents a "HID debug interface" that must be disregarded.
		const hidDevices = await navigator.hid.getDevices()
		const wantedProductId = parseUsbProductId(audioDevice.productId)
		const matchingDevice = findMatchingYealinkHid(hidDevices, wantedProductId)

		if (!matchingDevice) {
			// We do not have access - ask browser for access (narrowed to the selected model when known)
			let hidDevice: HIDDevice | undefined = undefined

			try {
				// Vendor ensures only Yealink devices are shown / 0x0005=Headset / 0x000B=telephony device
				const filters = [ wantedProductId != null
					? { usage: 0x0005, usagePage: 0x000B, vendorId: 0x6993, productId: wantedProductId }
					: { usage: 0x0005, usagePage: 0x000B, vendorId: 0x6993 } ];

				[ hidDevice ] = await navigator.hid.requestDevice({ filters })
			}
			catch (_) { /** Ignore */ }

			if (!hidDevice) {
				logger.error('Yealink: InitBrowser: Failed to get access to the headset - user probably rejected us')
				return
			}

			this.hidDevice = hidDevice
		} else {
			this.hidDevice = matchingDevice
		}

		// Test that we can connect and communicate
		try {
			if (!this.hidDevice.opened) {
				await this.hidDevice.open() // Open
			}
			await this.send(0) // OnHook
			await this.hidDevice.close() // Close
		} catch (err) {
			logger.error(`Yealink: InitBrowser: Failed to open communication with ${audioDevice.label} - ${(err as Error).message}`)
			return
		}

		this.use.value = true
		this.useDeviceName.value = this.hidDevice.productName

		window.addEventListener('beforeunload', this.boundClose)

		// A headset is granted as several HID interfaces sharing one product id - log which one call control was bound to
		const collections = this.hidDevice.collections?.map(collection => `0x${collection.usagePage?.toString(16)}:0x${collection.usage?.toString(16)}`).join(' ')

		logger.info(`Yealink: InitBrowser: Headset connected: ${this.useDeviceName.value} - ${this.hidDevice.vendorId}:${this.hidDevice.productId} - interface ${collections}`)
	}

	/** Is the headset connected */
	private get isOpen() { return this.hidDevice ? this.hidDevice.opened : false }

	/** Open a connection to the headset so that we can start to control it */
	private async open() {
		if (!this.hidDevice) { throw new Error('No device to open') }
		if (!this.hidDevice.opened) {
			await this.hidDevice.open()
			this.hidDevice.addEventListener('inputreport', this.boundHeadsetEvent as EventListener)
			logger.debug(`Yealink: ${this.useDeviceName.value} is open and we are listening for events`)
		}
	}

	/**
	 * Closes the connection to the headset when we are done controlling it
	 */
	private async close() {
		if (this.hidDevice?.opened) {
			await this.hidDevice.close()
			// Close seems to delete the hidDevice itself - don't know if it's always the case, so just putting an if statement here to be sure
			if (this.hidDevice) {
				this.hidDevice.removeEventListener('inputreport', this.boundHeadsetEvent as EventListener)
			}
			logger.debug(`Yealink: ${this.useDeviceName.value} is closed`)
		}
	}

	/**
	 * Handled events from the headset
	 * @param {Object} event Event listener report
	 */
	private async headsetEvent(event: HIDInputReportEvent) {
		const value = event.data.getUint8(0)
		// | console.log(`${device.productId} - ${device.productName} -- reportId: ${reportId} - value: ${value}`);

		if (event.reportId === 2) {
			switch (value) {
				case 2: // Answer button pressed
					logger.info('Yealink: User pressed answer')
					this.eventCallback('ANSWER')
					break
				case 0: // device is on-hook
					if (this.lastAction !== 0) {
						logger.info('Yealink: User pressed hangup')
						this.eventCallback('HANGUP')

						try { await this.close() }
						catch (_) { /** */ }
					} else {
						logger.info('Yealink: Last action was hangup, so we will not emit Call:Hangup')
					}
					break
				case 9: // Hold button pressed
					if (!this.holdActive) {
						this.holdActive = true
						logger.info('Yealink: User pressed hold')
						this.eventCallback('HOLD')
					} else {
						this.holdActive = false
						logger.info('Yealink: User pressed unhold')
						this.eventCallback('UNHOLD')
					}
					break
				case 1: // Device is off hook
					break
				case 5: // Mute pressed
					break
				default: // Dont care
					break
			}
		}
	}

	// #endregion

	// #region Actions - public

	// The call id and contact info are only used by the Electron/SDK integration, which mirrors every call on the device
	async offHook(_callId?: string, _name?: string, _number?: string, _answered?: boolean) {
		logger.info('Yealink: Telling headset to go off hook')

		try {
			await this.open()
			await this.send(1)
		}
		catch (err) { logger.error(`Yealink: Off hook failed - ${(err as Error).message}`) }
	}

	async onHook(_callId?: string) {
		logger.info('Yealink: Telling headset to go on hook')

		try {
			await this.send(0)
			await this.close()
		}
		catch (err) { logger.error(`Yealink: On hook failed - ${(err as Error).message}`) }
	}

	async ringOn(_callId?: string, _name?: string, _number?: string) {
		logger.info('Yealink: Telling headset to start ringer')

		try {
			await this.open()
			await this.send(4)
		}
		catch (err) { logger.error(`Yealink: Start ringer failed - ${(err as Error).message}`) }
	}

	async ringOff(_callId?: string) {
		logger.info('Yealink: Telling headset to stop ringer')

		try {
			await this.send(0)
			await this.close()
		}
		catch (err) { logger.error(`Yealink: Stop ringer failed - ${(err as Error).message}`) }
	}

	async hold(_callId?: string) {
		logger.info('Yealink: Telling headset to go on hold')

		try {
			await this.send(8 + 1)
		}
		catch (err) { logger.error(`Yealink: Hold failed - ${(err as Error).message}`) }
	}

	async unhold(_callId?: string) {
		logger.info('Yealink: Telling headset to unhold')

		try {
			if (!this.isOpen) {
				await this.open()
			}
			await this.send(1)
		}
		catch (err) { logger.error(`Yealink: Unhold failed - ${(err as Error).message}`) }
	}

	/** Mute control is not supported by the WebHID integration - present for interface parity with the Electron/SDK integration */
	async mute(_callId?: string) { /** Not supported over WebHID */ }

	/** Mute control is not supported by the WebHID integration - present for interface parity with the Electron/SDK integration */
	async unmute(_callId?: string) { /** Not supported over WebHID */ }

	async send(value: number) {
		await this.hidDevice?.sendReport(0x02, new Uint8Array([value]))
		this.lastAction = value
	}

	// #endregion
}
